import React, {ReactElement, useEffect, useMemo, useRef, useState} from "react"
import {debounce, isNumber} from "lodash-es"
import {observer} from "mobx-react-lite"
import classNames from "classnames"
import {t} from "@ui/common/intl"
import {IStore, useStore} from "@ui/stores"
import ProfileIcon from "@ui/icons/menu/down/Profile"
import TrackIcon from "@ui/icons/menu/down/Track"
import RouteIcon from "@ui/icons/menu/down/Route"
import AdapterIcon from "@ui/icons/menu/down/Adapter"
import SosIcon from "@ui/icons/menu/down/Sos"
import FriendIcon from "@ui/icons/menu/down/Friend"
import NeighbourIcon from "@ui/icons/menu/down/Neighbour"
import Poi from "@ui/icons/menu/down/Poi"
import {TMenuProps, MenuItem, TMenuItem} from "./"

const getMenuItems: () => TMenuItem[] = () => [
  {
    item: MenuItem.Profile,
    icon: <ProfileIcon />,
    title: "menu.profile",
  },
  {
    item: MenuItem.Track,
    icon: <TrackIcon />,
    title: "menu.track",
    selected: (store: IStore) => store.track.isRecording
  },
  {
    icon: <Poi />,
    item: MenuItem.Poi,
    title: "menu.poi"
  },
  {
    item: MenuItem.Friends,
    title: "menu.friends",
    icon: <FriendIcon disabled={true} />,
    disabled: true
  },
  {
    item: MenuItem.Neighbour,
    title: "menu.neighbour",
    icon: <NeighbourIcon disabled={true} />,
    disabled: true
  },
  {
    item: MenuItem.Adapter,
    title: "menu.adapter",
    icon: <AdapterIcon disabled={!process.env.TEST_BOAT_SOCKET_URL} />,
    disabled: !process.env.TEST_BOAT_SOCKET_URL
  },
  {
    item: MenuItem.Route,
    title: "menu.routes",
    icon: <RouteIcon disabled={true} />,
    disabled: true
  },
  {
    item: MenuItem.Sos,
    title: "menu.sos",
    icon: <SosIcon disabled={true} />,
    disabled: true
  }
]

const DownMenu = ({onCLick}: TMenuProps): ReactElement => {
  const store = useStore()
  const {settings} = store
  const pagesRef = useRef<HTMLDivElement>(null)
  const [show, setShow] = useState(false)
  const [page, setPage] = useState(0)

  const menuItems = getMenuItems()

  const itemsPerPage = settings.isMobile ? 4 : 6
  const pagesCount = Math.ceil(menuItems.length / itemsPerPage)
  const withPagination = menuItems.length > itemsPerPage

  useEffect(() => {
    setShow(true)
  }, [])

  const updateMenuTop = (timeout: number) => setTimeout(() => {
      const elem = pagesRef.current
      if (elem) settings.setMenuTop(elem.getBoundingClientRect().top)
    }, timeout)

  screen.orientation?.addEventListener("change", (e) => {
    updateMenuTop(100)
  })

  useEffect(() => {
    if (show) {
      updateMenuTop(1000)
    }
  }, [show])

  const handleScroll = useMemo(
    () => debounce((e: any) => {
      const elem = pagesRef.current
      if (elem && isNumber(elem.scrollLeft) && isNumber(elem.scrollWidth)) {
        const page = Math.round((elem.scrollLeft / elem.scrollWidth) * pagesCount)
        if (isNumber(page) && page >= 0 && page <= pagesCount - 1) setPage(page)
      }
    }, 100), []
  )

  return (
    <div className={classNames("menu", {"show": show, "with-pagination": withPagination})}>
        <div className="pages" onScroll={handleScroll} ref={pagesRef}>
          {
            Array(pagesCount)
              .fill(0)
              .map((val, i) => <div key={`menu-page-${i}`} className="items">
                {menuItems
                  .slice(i * itemsPerPage, (i + 1) * itemsPerPage)
                  .map(({item, icon, title, selected, disabled}, i) => {
                    return (
                      <div key={`menu-item-${i}`}>
                        <div className={classNames({"selected": selected && selected(store)})}></div>
                        <div
                          className={classNames("item", {"disabled": disabled})}
                          onClick={() => !disabled && onCLick(item)}
                        >
                          {icon}
                        </div>
                        <span>{t(title)}</span>
                      </div>
                    )
                  })
                }
              </div>)
          }
        </div>
        {withPagination
          ? (<div className="pagination">
            {
              Array(pagesCount)
                .fill(0)
                .map((val, i) => (
                  <div
                    key={`pagination-menu-items-${i}`}
                    className={classNames("page-circle", {"selected": page === i})}
                  />
                ))
            }
          </div>)
          : <></>
        }
      </div>
  )
}

export default observer(DownMenu)
